import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import {
  AiManagementCard,
  AiManagementState,
  AiManagementTeamMember,
} from 'store/modules/ai_management_store/types';
import { upsertCard } from 'store/modules/ai_management_store/actions';
import AiManagementAvatar from 'components/atoms/ai_management_avatar';
import AiManagementModalHeader from 'components/molecules/ai_management_modal_header';
import { addCardMember, removeCardMember } from 'services/actions/ai_management_cards';
import { Container } from './styles';

interface AiManagementMembersModalProps {
  card: AiManagementCard;
  show: boolean;
  onClose: () => void;
}

const AiManagementMembersModal: React.FC<AiManagementMembersModalProps> = ({
  card,
  show,
  onClose,
}) => {
  const dispatch = useDispatch();
  const team = useSelector<ApplicationState, AiManagementState['team']>(
    (state) => state.session.ai_management.team,
  );
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (show) setSearch('');
  }, [show]);

  const applyResponse = (res: any) => {
    if (!res || res.status >= 400) return;
    const updated = res.data?.card ?? res.data;
    if (updated?.id) dispatch(upsertCard(updated));
  };

  const addMember = (member: AiManagementTeamMember) => {
    addCardMember(card.id, member.id).then(applyResponse);
  };

  const removeMember = (id: number) => {
    removeCardMember(card.id, id).then(applyResponse);
  };

  const query = search.trim().toLowerCase();
  const currentIds = card.members.map((member) => member.id);
  const boardMembers = card.members.filter((member) => member.name.toLowerCase().includes(query));
  const teamMembers = team.filter(
    (member) => !currentIds.includes(member.id) && member.name.toLowerCase().includes(query),
  );

  return (
    <Modal show={show} onHide={onClose} centered style={{ zIndex: 3000000005 }}>
      <Container>
        <AiManagementModalHeader title="Alterar membros" onClose={onClose} />

        <div className="members-body p-4">
          <div className="members-input-box d-flex align-items-center pr-2 mb-3">
            <input
              type="text"
              className="form-control"
              placeholder="Pesquisar membros"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <button
              type="button"
              className="btn btn-link p-0 text-primary flex-shrink-0"
              onClick={() => setSearch('')}
            >
              Limpar
            </button>
          </div>

          <div className="font-weight-bold mb-2">Membros da licitação</div>
          {boardMembers.length === 0 && <div className="text-muted small mb-2">Nenhum membro.</div>}
          {boardMembers.map((member) => (
            <div key={member.id} className="d-flex align-items-center py-2">
              <AiManagementAvatar
                initials={member.initials}
                memberId={member.id}
                size={32}
                className="mr-2"
              />
              <span className="flex-grow-1">{member.name}</span>
              <button
                type="button"
                className="btn btn-link p-0 text-muted d-flex align-items-center"
                aria-label={`Remover ${member.name}`}
                onClick={() => removeMember(member.id)}
              >
                <i className="material-icons">close</i>
              </button>
            </div>
          ))}

          <div className="font-weight-bold mb-2 mt-3">Membros da equipe</div>
          {teamMembers.length === 0 && (
            <div className="text-muted small">Nenhum contato disponível.</div>
          )}
          {teamMembers.map((member) => (
            <button
              key={member.id}
              type="button"
              className="members-team-row d-flex align-items-center"
              onClick={() => addMember(member)}
            >
              <AiManagementAvatar
                initials={member.initials}
                memberId={member.id}
                size={32}
                className="mr-2"
              />
              <span className="flex-grow-1 text-left">{member.name}</span>
            </button>
          ))}
        </div>
      </Container>
    </Modal>
  );
};

export default AiManagementMembersModal;
